import type { AxiosCustomResponse } from '../types.js';
import { objectKeysToCamelCase } from '../utils/objectKeysTo.js';

export function responseDataToCamelCase(
  response: AxiosCustomResponse,
): AxiosCustomResponse {
  // Flag is flat-spread onto the axios config via upstream's makeConfig
  // from our fork's ClientOptions (`@mint-lib/api-zod-support`).
  const camelCaseBody = (
    response.config as { response?: { camelCaseBody?: boolean } }
  ).response?.camelCaseBody;
  if (camelCaseBody === false) {
    return response;
  }

  // Skip binary/typed payloads (Blob, File, ArrayBuffer, FormData, Streams).
  // objectKeysToCamelCase would traverse them via `Object.keys()` and return an
  // empty `{}`, clobbering the payload. Stream endpoints rely on this guard.
  if (!isPlainJsonPayload(response.data)) {
    return response;
  }

  return {
    ...response,
    data: objectKeysToCamelCase(response.data),
  };
}

function isPlainJsonPayload(data: unknown): boolean {
  if (data == null) return true;
  if (Array.isArray(data)) return true;
  if (typeof data !== 'object') return true;
  // Treat any known browser/Node binary container as non-plain.
  if (typeof Blob !== 'undefined' && data instanceof Blob) return false;
  if (typeof File !== 'undefined' && data instanceof File) return false;
  if (typeof FormData !== 'undefined' && data instanceof FormData) return false;
  if (typeof ArrayBuffer !== 'undefined') {
    if (data instanceof ArrayBuffer) return false;
    if (ArrayBuffer.isView(data)) return false;
  }
  if (typeof ReadableStream !== 'undefined' && data instanceof ReadableStream) {
    return false;
  }
  return true;
}
